import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';
import { OktaAuth } from '@okta/okta-auth-js';

export type OktaLoginProps = BoKNavigation.OktaLoginWidget & {
  fields?: {
    children: LoginSubFolder[];
  };
  onSuccess?: (res: unknown) => void;
  onError?: (err: unknown) => void;
};

export type LoginSubFolder = {
  templateId?: string;
  fields: {
    children: LoginLinkCollection[];
  };
};

export type LoginLinkCollection = BoKNavigation.NavigationLink & {
  fields: {
    children: BoKNavigation.NavigationLink[];
  };
};

export type AdditionalLinksCollection = BoKNavigation.LoginModalLinks & {
  fields: {
    children: LoginLinkCollection[];
  };
};
export interface OktaTokens {
  id_token: string;
  access_token: string;
  okta_function: string;
  session_token: string;
  state_token: string;
  mfa_factor_type: string;
  user_name: string;
}

// Type for Okta Sign-In Widget instance
export interface OktaSignInWidget {
  remove: () => void;
  renderEl: (
    options: { el: string },
    success: (result: unknown) => void,
    error: (err: unknown) => void
  ) => void;
}

// Helper function to determine Okta status from response
export const getOktaStatus = (status: string): string => {
  switch (status) {
    case 'SUCCESS':
      return 'LOGIN_SUCCESS';
    case 'MFA_REQUIRED':
    case 'MFA_ENROLL':
    case 'FACTOR_REQUIRED':
      return 'MFA_CHALLENGE';
    case 'PASSWORD_EXPIRED':
      return 'PASSWORD_EXPIRED';
    case 'LOCKED_OUT':
      return 'LOCKED_OUT';
    default:
      return '';
  }
};

// Helper function to create tokens from Okta response
export const createOktaTokens = (data: unknown, oktaStatus: string): OktaTokens => {
  const typedData = data as {
    idToken?: string;
    accessToken?: string;
    sessionToken?: string;
    stateToken?: string;
    _embedded?: {
      factors?: Array<{ factorType?: string }>;
      user?: {
        profile?: {
          login?: string;
        };
      };
    };
  };

  const user_name = typedData._embedded?.user?.profile?.login ?? '';

  return {
    id_token: typedData.idToken ?? '',
    access_token: typedData.accessToken ?? '',
    okta_function: oktaStatus ?? '',
    session_token: typedData.sessionToken ?? '',
    state_token: typedData.stateToken ?? '',
    mfa_factor_type: typedData._embedded?.factors?.[0]?.factorType ?? 'SMS',
    user_name: user_name,
  };
};

function checkDeviceToken() {
  let token = localStorage.getItem('DEVICE_FINGERPRINT');
  if (!token) {
    token = CreateGuid();
    localStorage.setItem('DEVICE_FINGERPRINT', token);
  }
}

function CreateGuid() {
  return _p8(false) + _p8(true) + _p8(false);
}

function _p8(s: boolean) {
  const p = (Math.random().toString(16) + '000000000').substring(2, 8);
  return s ? '-' + p.substring(0, 4) + '-' + p.substring(4, 4) : p;
}

// Helper function to patch request body with device token
export const patchRequestBody = (body: string): string => {
  checkDeviceToken();
  let parsedData: unknown;
  try {
    parsedData = JSON.parse(body);
  } catch {
    return body;
  }

  if (
    parsedData &&
    typeof parsedData === 'object' &&
    'password' in parsedData &&
    'username' in parsedData
  ) {
    const pd = parsedData as {
      [key: string]: unknown;
      password: string;
      username: string;
      options?: Record<string, unknown>;
    };
    let deviceToken: string | undefined;
    if (typeof window !== 'undefined') {
      try {
        deviceToken = localStorage.getItem('DEVICE_FINGERPRINT') || undefined;
      } catch {
        deviceToken = undefined;
      }
    }
    const newData = {
      ...pd,
      idpProperties: { deviceToken },
      domain: 'consumer',
      options: {
        ...(pd.options || {}),
        warnBeforePasswordExpired: false,
      },
    };
    return JSON.stringify(newData);
  }

  return body;
};

// Helper function to check if response is from Okta auth endpoint
export const isOktaAuthResponse = (response: Response, eisDomain: string): boolean => {
  return (
    response.status === 200 &&
    'url' in response &&
    typeof response.url === 'string' &&
    !!eisDomain &&
    response.url.includes(eisDomain)
  );
};

// Helper function to build Okta widget configuration
export const buildOktaConfig = (props: OktaLoginProps) => {
  return {
    brandName: 'BOKF',
    flow: 'login' as const,
    baseUrl: props?.fields?.EisLink?.value?.href || '',
    clientId: process.env.OKTA_LOGIN_CLIENT_ID ?? '',
    useClassicEngine: true,
    language: 'en',
    authParams: {
      responseType: ['token', 'id_token'] as ('token' | 'id_token')[],
      display: 'page' as const,
      scopes: ['openid', 'profile'],
    },
    helpLinks: {
      forgotPassword: props?.fields?.ForgotUsernameLink?.value?.href || '',
      help: props?.fields?.ForgotUsernameLink?.value?.href || '',
      unlock: '',
    },
    i18n: {
      en: {
        'primaryauth.title': props?.fields?.UsernamePlaceholder?.value,
        'primaryauth.submit': props?.fields?.ButtonText?.value,
        'primaryauth.username.placeholder': props?.fields?.UsernamePlaceholder?.value,
        'primaryauth.password.placeholder': props?.fields?.PasswordPlaceholder?.value,
        'errors.E0000004': props?.fields?.InvalidCredentials?.value,
        remember: props?.fields?.RememberUsernameText?.value,
      },
    },
    features: {
      selfServiceUnlock: false,
      showPasswordToggleOnSignInPage: true,
      autoFocus: true,
      rememberMe: true,
      hideSignOutLinkInMFA: true,
      showUnlockAccountLink: false,
    },
  };
};

// Inject forgot password link
export const createForgotLink = (widgetId: string, href: string): (() => void) => {
  const addForgotLink = () => {
    const existingForgotLink = document.getElementById(`${widgetId}-forgot-link`);

    if (!existingForgotLink) {
      const widgetContainer = document.querySelector(`.o-form-fieldset-container`);
      if (widgetContainer) {
        const forgotLink = document.createElement('div');
        forgotLink.id = `${widgetId}-forgot-link`;
        forgotLink.className = 'okta-signin-widget-forgot-link';
        forgotLink.innerHTML = `<a href="${
          href || '#'
        }" tabindex="0">Forgot username or password?</a>`;
        widgetContainer.appendChild(forgotLink);
      } else {
        setTimeout(addForgotLink, 100);
      }
    }
  };

  return addForgotLink;
};

// Show/hide caps lock warning in error container
export const toggleCapsLockWarning = (widgetId: string, isCapsLockOn: boolean): void => {
  const errorContainer = document.getElementById(`${widgetId}-error-summary`);
  if (!errorContainer) return;

  const capsLockWarningId = `${widgetId}-caps-lock-warning`;
  let capsLockWarning = document.getElementById(capsLockWarningId);

  if (isCapsLockOn) {
    if (!capsLockWarning) {
      capsLockWarning = document.createElement('div');
      capsLockWarning.id = capsLockWarningId;
      capsLockWarning.className = 'error-item okta-caps-lock-warning';
      capsLockWarning.innerHTML =
        '<span class="icon icon-16 error-16-small"></span>Caps lock is on';
      errorContainer.insertBefore(capsLockWarning, errorContainer.firstChild);
    }
    errorContainer.style.display = 'block';
  } else {
    if (capsLockWarning) {
      capsLockWarning.remove();
    }

    const otherErrors = errorContainer.querySelectorAll('.error-item:not(.okta-caps-lock-warning)');
    if (otherErrors.length === 0) {
      errorContainer.style.display = 'none';
    }
  }
};

// Setup caps lock detection
export const setupCapsLockDetection = (
  widgetId: string,
  passwordInput: HTMLInputElement
): (() => void) => {
  const checkCapsLock = (event: KeyboardEvent) => {
    const isCapsLockOn = event.getModifierState?.('CapsLock');
    toggleCapsLockWarning(widgetId, !!isCapsLockOn);
  };

  const handleBlur = () => {
    toggleCapsLockWarning(widgetId, false);
  };

  const handleFocus = () => {
    const checkOnFirstKey = (event: KeyboardEvent) => {
      checkCapsLock(event);
    };
    passwordInput.addEventListener('keydown', checkOnFirstKey, { once: true });
  };

  const handleClick = (event: MouseEvent) => {
    if (event.getModifierState?.('CapsLock')) {
      toggleCapsLockWarning(widgetId, true);
    }
  };

  passwordInput.addEventListener('keydown', checkCapsLock);
  passwordInput.addEventListener('keyup', checkCapsLock);
  passwordInput.addEventListener('focus', handleFocus);
  passwordInput.addEventListener('blur', handleBlur);
  passwordInput.addEventListener('click', handleClick);

  return () => {
    passwordInput.removeEventListener('keydown', checkCapsLock);
    passwordInput.removeEventListener('keyup', checkCapsLock);
    passwordInput.removeEventListener('focus', handleFocus);
    passwordInput.removeEventListener('blur', handleBlur);
    passwordInput.removeEventListener('click', handleClick);
  };
};

// Add caps lock warning with retry logic
export const createCapsLockWarning = (widgetId: string): (() => void) => {
  const addCapsLockWarning = (): (() => void) | undefined => {
    const passwordInput = document.querySelector(
      `#${widgetId} input[name="password"]`
    ) as HTMLInputElement;

    if (!passwordInput) {
      setTimeout(addCapsLockWarning, 100);
      return;
    }

    const existingWarning = document.getElementById(`${widgetId}-caps-lock-warning`);
    if (existingWarning) {
      return;
    }

    return setupCapsLockDetection(widgetId, passwordInput);
  };

  return addCapsLockWarning;
};

// Make the password visibility toggle keyboard accessible
export const setupPasswordToggleAccessibility = (widgetId: string): (() => void) => {
  const root = document.getElementById(widgetId)?.closest('#okta-sign-in') || document;
  if (!root) return () => {};

  let cleanup: (() => void) | undefined;

  const enhanceToggle = (): void => {
    const toggleButton =
      root.querySelector<HTMLElement>('.o-form .input-fix .password-toggle .button-show') ||
      root.querySelector<HTMLElement>('.o-form .input-fix .password-toggle .button-hide') ||
      root.querySelector<HTMLElement>('.o-form .input-fix .password-toggle .eyeicon');

    if (!toggleButton) {
      // Retry shortly if the widget hasn't finished rendering
      setTimeout(enhanceToggle, 100);
      return;
    }

    // Make it behave like a button (if not already)
    if (!toggleButton.hasAttribute('tabindex')) {
      toggleButton.tabIndex = 0;
    }
    if (!toggleButton.hasAttribute('role')) {
      toggleButton.setAttribute('role', 'button');
    }

    // Helper to sync ARIA label with current state
    const updateAriaLabel = () => {
      const passwordInput = root.querySelector<HTMLInputElement>('input[name="password"]');
      const isVisible = passwordInput?.type === 'text';
      toggleButton.setAttribute('aria-label', isVisible ? 'Hide password' : 'Show password');
      toggleButton.setAttribute('aria-pressed', isVisible ? 'true' : 'false');
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();

        const target = event.currentTarget as HTMLElement | null;
        if (!target) return;

        // Prefer dispatching a real click on the inner eye icon if present
        const eye = target.querySelector<HTMLElement>('.eyeicon') || target;
        const syntheticClick = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        });
        eye.dispatchEvent(syntheticClick);

        // Fallback: manually toggle password input type if needed
        const passwordInput = root.querySelector<HTMLInputElement>('input[name="password"]');
        if (passwordInput && passwordInput.type === 'password') {
          passwordInput.type = 'text';
        } else if (passwordInput && passwordInput.type === 'text') {
          passwordInput.type = 'password';
        }

        updateAriaLabel();
      }
    };

    toggleButton.addEventListener('keydown', handleKeyDown);
    // Ensure ARIA state is correct on initial render
    updateAriaLabel();

    cleanup = () => {
      toggleButton.removeEventListener('keydown', handleKeyDown);
    };
  };

  enhanceToggle();

  return () => {
    if (cleanup) cleanup();
  };
};

// Update error display
export const updateErrorDisplay = (widgetId: string, errorText: string): void => {
  const fieldErrors = document.querySelectorAll(`#${widgetId} .o-form-input-error.o-form-explain`);
  const formErrorContainer = document.querySelector(`#${widgetId} .o-form-error-container`);
  const errorContainer = document.getElementById(`${widgetId}-error-summary`);

  if (fieldErrors.length > 0 || formErrorContainer?.classList.contains('o-form-has-errors')) {
    for (const error of fieldErrors) {
      (error as HTMLElement).style.display = 'none';
    }

    if (formErrorContainer) {
      (formErrorContainer as HTMLElement).style.display = 'none';
    }

    if (errorContainer) {
      errorContainer.innerHTML = `<div class="error-item"><span class="icon icon-16 error-16-small"></span>${errorText}</div>`;
      errorContainer.style.display = 'block';
    }
  } else if (errorContainer) {
    errorContainer.style.display = 'none';
    errorContainer.innerHTML = '';

    const hiddenErrors = document.querySelectorAll(
      `#${widgetId} .o-form-input-error.o-form-explain`
    );
    for (const error of hiddenErrors) {
      (error as HTMLElement).style.display = '';
    }

    if (formErrorContainer) {
      (formErrorContainer as HTMLElement).style.display = '';
    }
  }
};

// Setup error observation
export const createErrorObserver = (widgetId: string, errorText: string): (() => void) => {
  const observeErrors = (): (() => void) | undefined => {
    const authFooter = document.querySelector(`#${widgetId} .auth-footer`);
    if (!authFooter) {
      setTimeout(observeErrors, 100);
      return;
    }

    let errorContainer = document.getElementById(`${widgetId}-error-summary`);
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.id = `${widgetId}-error-summary`;
      errorContainer.className = 'okta-error-summary';
      errorContainer.style.display = 'none';
      errorContainer.setAttribute('role', 'alert');
      errorContainer.setAttribute('aria-live', 'assertive');
      authFooter.appendChild(errorContainer);
    }
    let pending = false;
    const updateErrors = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        updateErrorDisplay(widgetId, errorText);
      });
    };
    const observer = new MutationObserver(updateErrors);
    const formContainer = document.querySelector(`#${widgetId} .o-form-fieldset-container`);
    if (formContainer) {
      observer.observe(formContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    updateErrors();

    return () => {
      observer.disconnect();
    };
  };

  return observeErrors;
};

// Logout current Okta session (prevents auto-redirect on marketing site)
export const logout = async (reload: boolean = true): Promise<void> => {
  const issuer = process.env.OKTA_LOGIN_ISSUER ?? '';
  const authClientConfig = { issuer };
  const authClient = new OktaAuth(authClientConfig);
  try {
    await authClient.revokeAccessToken();
  } catch {
    /* ignore revoke errors */
  }
  try {
    const sessionClosed = await authClient.closeSession();
    if (sessionClosed && reload && typeof window !== 'undefined') {
      setTimeout(() => window.location.reload(), 50);
    }
  } catch (e: unknown) {
    const status =
      typeof e === 'object' && e !== null && 'xhr' in e
        ? (e as { xhr?: { status?: number } }).xhr?.status
        : undefined;
    if (status === 429) {
      // too many requests - ignore
    }
  }
};

// Submit tokens to banking endpoint
export const submitForm = (tokens: OktaTokens, submitLink: string): void => {
  const form = document.createElement('form');
  form.id = 'formSignIn';
  form.method = 'POST';
  form.action = submitLink || '';

  const addHidden = (name: keyof OktaTokens) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.id = name;
    input.value = tokens[name];
    form.appendChild(input);
  };

  addHidden('id_token');
  addHidden('access_token');
  addHidden('okta_function');
  addHidden('state_token');
  addHidden('mfa_factor_type');
  addHidden('session_token');
  addHidden('user_name');

  document.body.appendChild(form);
  form.submit();
};

// Hide Okta widget prior to redirect / form submission
export const hideOktaWidget = (widgetId: string): void => {
  const el = document.getElementById(widgetId);
  if (el) {
    el.style.display = 'none';
  }
  const container = el?.closest('.okta-sign-in');
  if (container) {
    (container as HTMLElement).style.visibility = 'hidden';
  }
};

// Factory: create auth success handler
export const createHandleAuthSuccess = (
  props: OktaLoginProps,
  logoutImmediately: boolean = true,
  widgetId: string = 'okta-signin-widget'
): ((result: unknown) => void) => {
  return (result: unknown) => {
    const typedResult = result as { status?: string; tokens?: unknown };
    if (typedResult.status !== 'SUCCESS') return;
    if (logoutImmediately) {
      logout();
      return;
    }
    // Hide widget before preparing and submitting tokens
    hideOktaWidget(widgetId);

    const tokensResult = typedResult as {
      tokens?: {
        idToken?: { idToken?: string };
        accessToken?: { accessToken?: string };
      };
    };

    if (!tokensResult.tokens) return;

    const customResult = result as {
      oktafunction?: string;
      sessionToken?: { sessionToken?: string };
      stateToken?: { stateToken?: string };
      mfaFactorType?: { mfaFactorType?: string };
      _embedded?: { user?: { profile?: { login?: string } } };
    };

    const user_name = customResult._embedded?.user?.profile?.login ?? '';

    const tokens: OktaTokens = {
      id_token: tokensResult.tokens.idToken?.idToken ?? '',
      access_token: tokensResult.tokens.accessToken?.accessToken ?? '',
      okta_function: customResult.oktafunction ?? '',
      session_token: customResult.sessionToken?.sessionToken ?? '',
      state_token: customResult.stateToken?.stateToken ?? '',
      mfa_factor_type: customResult.mfaFactorType?.mfaFactorType ?? '',
      user_name,
    };
    submitForm(tokens, props?.fields?.SubmitLink?.value?.href || '');
  };
};
